import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { api } from './lib/api';
import { CvWorkspace } from './CvWorkspace';
import { Landing } from './Landing';
import { Account, type Me } from './Account';
import { ImportCv } from './ImportCv';
import { ImportConfirm, type Candidate, type CommitEntry } from './ImportConfirm';
import { ImportExceptions, type ContactCandidate, type CoverageChunk, type DuplicateSuspect } from './ImportExceptions';
import { ParsingIndicator } from './ParsingIndicator';
import { parseErrorMessage } from './lib/parseError';
import { EvalSetup } from './EvalSetup';
import { GuidedStart } from './GuidedStart';
import { GuidedAuthor } from './GuidedAuthor';
import { CvPane } from './CvPane';
import { ImgPanel } from './ImgPanel';
import { QualityReadView } from './QualityReadView';
// R5.2 (spec §3) — the Evaluate flow's "review" destination now goes through the new rubric read surface, which
// is gated by the SERVER's own cohort decision (§8.3): it asks for the read, and a typed 403 (or an unrecognized
// answer from a backend that does not have the routes yet) means this account is not in the cohort and gets the
// legacy view below, unchanged. The client holds no flag constant and caches no decision.
import { RubricReadView } from './rubric/RubricReadView';
import { LegalPage } from './LegalPage'; // Trust/Legal P1 — /privacy + /terms, publicly readable; Polish B2 adds /refunds
import { Help } from './Help'; // Polish B2 — /help, publicly readable
import { PaywallModal } from './PaywallModal';
import { IconBack, IconDown, IconClose, IconMic, IconStop, IconCheck } from './components/icons';
// cc-11 round 14 (Sol blocker 2) — the standing quantity note. The stage-1 prompt TELLS the model "the product
// itself shows a standing note under every quantity question", which is why it is allowed to ask a bare "how many"
// without weaving an escape route into the question text (cc-7, Sol round-4). That claim was true only on
// QualityReadView; this surface consumes the SAME classifier output via /api/classify, so it renders it too.
import { isQuantityAsk, QUANTITY_HINT_COPY } from './quantityHint';
import { type CoverageRead } from './lib/types'; // the type still gates the evaluate-read spinner; the panel is replaced by QualityReadView
import * as checkoutContext from './lib/checkoutContext'; // Revenue P0 (P0-1) — checkout/auth continuity

// Phase 3 — multi-section authoring (the full Stage-1 CV). Pick a specialty, then author entries across
// the canonical section taxonomy. archetype routes the engine: EXPERIENCE sections use the Phase 2 engine
// (verb guidance + type a bullet + failure-mode diagnosis); CREDENTIAL sections are structured facts +
// an optional integrity-checked citation. Verb examples are GUIDANCE only — never insertable.

interface Specialty { id: string; name: string }
interface Section { id: string; name: string; sort_order: number; archetype: 'experience' | 'credential' }
interface Gate { gate_id: string; status: 'pass' | 'flag'; diagnosis: string; question: string }
interface ClassifyResult { overall: 'pass' | 'needs_attention'; ladder: Gate; integrity: Gate[] }
interface SavedEntry { section: string; sectionName: string; archetype: string; title: string; organization: string; bullets: string[] }
interface CvSummary {
  id: string; specialty_id: string; specialty_name: string; created_at: number; last_edited: number; entry_count: number;
  // B3 "Your CVs" library fields:
  name?: string | null; source_filename?: string | null;
  has_assessment?: boolean; overall?: string | null; assessed_at?: number | null; stale_hint?: boolean;
  is_paid?: boolean; // Revenue P0 (P0-1) — the checkout-success poll's single-tier signal
}

// Phase 9b — voice capture guards. CF's audio limits are unpublished; cap client-side (a couple of minutes is far
// more than describing one entry needs) and reject an oversized blob before upload, rather than hit CF's ceiling.
const MAX_REC_SECONDS = 120;
const MAX_AUDIO_BYTES = 20 * 1024 * 1024;
const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const VERB_GUIDANCE = [
  { level: 'High ownership', examples: 'Led · Designed · Initiated · Built · Founded' },
  { level: 'Shared / coordinating', examples: 'Coordinated · Managed · Implemented · Organized' },
  { level: 'Supporting', examples: 'Contributed to · Supported · Assisted with · Collaborated on' },
];

// Coverage gap → where to ADD for it: a sensible default section (preselected in authoring) + a plain-language
// phrase for the banner. communication & specialty-commitment have no clean section → land with just the phrase.
const DIM_SECTION: Record<string, { section: string; phrase: string }> = {
  'research-scholarship': { section: 'research-and-scholarly-activity', phrase: 'research or scholarly work' },
  'clinical': { section: 'clinical-experience', phrase: 'clinical or hands-on care' },
  'teaching-education': { section: 'teaching-experience', phrase: 'teaching or mentoring' },
  'leadership': { section: 'leadership-and-administrative', phrase: 'leading or organizing something' },
  'advocacy-community': { section: 'volunteer-and-community-involvement', phrase: 'work with communities' },
  'communication-interpersonal': { section: '', phrase: 'communication-focused work' },
  'breadth': { section: 'extracurricular-activities-and-interests', phrase: 'interests or activities outside medicine' },
  'specialty-commitment': { section: '', phrase: 'experience specific to this specialty' },
};

// Viewport hook — inline style objects can't be media-queried, so the mobile header tidy-up reads the viewport in JS.
// matchMedia is genuinely emulated by the local preview's viewport resize (the standing PSCraft lesson), so it flips
// under test. SSR-safe guard for the initial value; subscribes to changes.
function useIsNarrow(maxWidth = 480): boolean {
  // Guard matchMedia: jsdom (the test env) doesn't implement it — fall back to desktop (false) rather than throw.
  const mqOf = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(`(max-width: ${maxWidth}px)`)
      : null;
  const [narrow, setNarrow] = useState(() => mqOf()?.matches ?? false);
  useEffect(() => {
    const mq = mqOf();
    if (!mq) return;
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [maxWidth]);
  return narrow;
}

// ATOMIC BULLETS — the import commit no longer splits on the client. It sends the parsed `descriptive` as one raw
// block and the SERVER splits on the "\n\n" LIST-ITEM boundary (workers/src/lib/splitListItems). The former
// client-side sentence-level split (`splitPoints`) over-split the evaluate funnel; see commitImport + the CHANGELOG.

export function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [specialtyId, setSpecialtyId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [title, setTitle] = useState('');
  const [organization, setOrganization] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateText, setDateText] = useState('');
  const [describe, setDescribe] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [draft, setDraft] = useState('');
  const [result, setResult] = useState<ClassifyResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [bullets, setBullets] = useState<string[]>([]);
  const [saved, setSaved] = useState<SavedEntry[]>([]);
  const [error, setError] = useState('');
  const [cvId, setCvId] = useState('');
  const [contact, setContact] = useState({ name: '', phone: '', city: '' });
  const [exportFormat, setExportFormat] = useState<'docx' | 'pdf'>('docx');
  const [addCrafted, setAddCrafted] = useState(false);
  // Views (state-as-router; no react-router). 'hub' = the two-door landing reachable while signed in; 'craft' =
  // the Craft door's start-fresh/import choice; 'evaluate' = the Evaluate door (P2-P5 stub for now).
  // Trust/Legal P1 — 'privacy' | 'terms' are STATE-AS-ROUTER views like the rest, but publicly readable (no auth
  // gate). Lazy-initialized from location.pathname so a fresh load of /privacy or /terms renders the legal page
  // immediately on first paint — no "Loading…" flash while the auth check resolves (unlike /checkout/success,
  // these must work signed OUT too).
  const [view, setView] = useState<'author' | 'review' | 'account' | 'import' | 'hub' | 'craft' | 'evaluate-read' | 'guided' | 'guided-author' | 'checkout-success' | 'privacy' | 'terms' | 'refunds' | 'help'>(() => {
    if (typeof window === 'undefined') return 'author';
    const p = window.location.pathname;
    if (p === '/privacy') return 'privacy';
    if (p === '/terms') return 'terms';
    // Polish B2 — /refunds and /help are STATE-AS-ROUTER views like Privacy/Terms: publicly readable, boot-path
    // routed the same way (no auth gate — a signed-out visitor can land here from the footer or a direct link).
    if (p === '/refunds') return 'refunds';
    if (p === '/help') return 'help';
    return 'author';
  });
  // B2 fix — captured ONCE from the boot-path lazy init above: true when the initial load landed on a PUBLIC
  // route (privacy/terms/refunds/help). These routes are reachable while authed too (a signed-in user can open
  // /help from the footer, or bookmark /privacy) and must not be clobbered by the post-auth effect's door-intent
  // routing or Phase-6 single-cv auto-restore below — both of which otherwise fire on every `authed` transition
  // and would setView() the user straight onto the workspace. A ref (not state) so it can't itself trigger a
  // re-render or drift after mount; it reflects the URL at first paint, which is the only thing that matters here.
  const bootedPublicRoute = useRef(
    typeof window !== 'undefined' && ['/privacy', '/terms', '/refunds', '/help'].includes(window.location.pathname)
  );
  // Brand B1 — per-view document.title. 'author'/'hub'/'craft' share the default (landing/pre-CV states);
  // guided authoring (both the interview and the entry-by-entry conversation) share one title.
  useEffect(() => {
    const TITLE_BY_VIEW: Record<string, string> = {
      review: 'Your CV — CVCraft',
      'evaluate-read': 'Evaluation — CVCraft',
      import: 'Import — CVCraft',
      account: 'Account — CVCraft',
      privacy: 'Privacy — CVCraft',
      terms: 'Terms — CVCraft',
      refunds: 'Refund Policy — CVCraft',
      help: 'Help — CVCraft',
      'checkout-success': 'Checkout — CVCraft',
      guided: 'Build your CV — CVCraft',
      'guided-author': 'Build your CV — CVCraft',
    };
    document.title = TITLE_BY_VIEW[view] ?? 'CVCraft — your CaRMS CV';
  }, [view]);
  // Conversational Phase 0 — the interview's stream answer ('img' | 'cmg'), threaded into the FIRST entry save so the
  // craft-minted CV carries it (the server sets stream on CV creation only; the craft path never sent one before).
  const [craftStream, setCraftStream] = useState('');
  // Conversational Phases 1–4 — which conversation the guided-author view runs: 'author' (build entry by entry after
  // the Phase-0 interview) or 'refine' (seed-from-import: walk the parsed entries together).
  const [guidedMode, setGuidedMode] = useState<'author' | 'refine'>('author');
  // Conversational Phase 5 — the live CV pane beside the conversation. cvPaneKey refetches the render model after
  // every save; lastSavedEntryId drives the pane's highlight; paneTab is the <900px one-pane-at-a-time toggle.
  const splitNarrow = useIsNarrow(900);
  const [cvPaneKey, setCvPaneKey] = useState(0);
  const [lastSavedEntryId, setLastSavedEntryId] = useState('');
  const [paneTab, setPaneTab] = useState<'talk' | 'cv'>('talk');
  const [paneCount, setPaneCount] = useState(0);
  const narrow = useIsNarrow();                       // mobile-header tidy-up (375px): compact account label + tighter padding
  const [readGated, setReadGated] = useState(false);  // FIX 2: the parse-quality warning gates the read → gate the sibling ImgPanel too
  // Payment Phase 2/3 — the paywall. Set to a cv_id when a craft/re-eval/export action returns 402 needs_paid_cv.
  const [paywallCvId, setPaywallCvId] = useState<string | null>(null);
  // Redeem→re-eval resync: bumped on a successful credit redeem so the read view (which 402'd) re-fires its load against
  // the now-paid gate — no manual refresh. Passed to QualityReadView/CvWorkspace, whose load effects depend on it.
  const [readReloadKey, setReadReloadKey] = useState(0);
  // Returns true (and opens the paywall) when a response is the per-CV paywall 402; callers then bail out.
  const maybePaywall = (res: { status: number; data: unknown }, fallbackCvId?: string): boolean => {
    const d = res.data as { reason?: string; cv_id?: string } | null;
    if (res.status === 402 && d?.reason === 'needs_paid_cv') {
      // Revenue P0 (P0-1) — the classic-craft 402 dropped the just-minted cv_id (the 402 payload carries it, e.g.
      // POST /api/entries mints a NEW cv then gates it). Keep it in state so redeem/unlock targets THAT cv instead
      // of a later save minting yet another one.
      if (d?.cv_id) setCvId(d.cv_id);
      setPaywallCvId(d.cv_id ?? fallbackCvId ?? cvId ?? null);
      return true;
    }
    return false;
  };
  // P4a — the import flow is SHARED; importMode routes the post-commit step: 'craft' → authoring; 'evaluate' →
  // the read (tag → coverage → render). evalRead holds the coverage read; evalBusy = the tagger pass in flight.
  const [importMode, setImportMode] = useState<'craft' | 'evaluate'>('craft');
  // R5.3 (spec §3) — "THIS CV JUST CAME FROM THE IMPORT FLOW", which is what turns the fresh-CV journey on.
  //
  // NOT A COHORT CHECK, AND DELIBERATELY NOT ONE (round-2 finding 1, §12a). `App.tsx` holds no opinion about
  // whether this account is in the R5 cohort — that decision is the SERVER's, consumed from the bootstrap by
  // `RubricReadView` itself, and a second copy of it here is exactly the drift that finding closed. What this
  // flag carries is the one fact only the parent knows: the user arrived here by importing a CV, not by opening
  // one from their list. `RubricReadView` uses it ONLY inside the state it reaches after the server has already
  // said "cohort" and "no ledger", so extraction can start for someone who just asked for a review and cannot
  // start for someone who merely opened a page — which matters, because that call is paywalled, costs an LLM
  // round trip, and binds the account's one free demo to this CV permanently (R5-OQ-C).
  const [freshImport, setFreshImport] = useState(false);
  const [evalRead, setEvalRead] = useState<CoverageRead | null>(null);
  const [evalBusy, setEvalBusy] = useState(false);
  // C2 — the tagger pass (POST /tags/cv/:id/infer) is now transactional server-side: a failure returns 502 and
  // PRESERVES the existing tags instead of silently deleting them. Surface that here too, so the coverage lines
  // aren't read as complete when the tagger didn't actually run this pass.
  const [tagsIncomplete, setTagsIncomplete] = useState(false);
  const [gapPrompt, setGapPrompt] = useState(''); // set when arriving in authoring from a coverage gap (banner + section preselect)
  // Identity: the full /api/auth/me payload (was discarded — only r.ok was read). Powers the header email cue
  // + the account view. Fetched in the [authed] effect, so every login path (bootstrap, verify, dev-login) sets it.
  const [me, setMe] = useState<Me | null>(null);
  // Evaluate/import — the craft-import state machine: ImportCv (ingest) → parse (P2a) → ImportConfirm → commit.
  const [importedText, setImportedText] = useState('');     // text confirmed in ImportCv; '' = still ingesting
  const [candidates, setCandidates] = useState<Candidate[] | null>(null); // P2a's parsed candidates; null = not parsed yet
  const [importBusy, setImportBusy] = useState(false);       // parse or commit in flight
  // C3a — how many entries /api/parse discarded entirely (pure noise) on THIS import. EVALUATE-only, non-blocking:
  // surfaced as a note on the evaluate-read view so a silent undercount can't masquerade as a reliable read. Persists
  // for the session (cleared on the next enterImport/parseImport, not on view navigation).
  const [parseDropped, setParseDropped] = useState(0);
  // G1-1c — silent-loss exceptions from THIS parse: a contact_candidate field the current profile lacks, source
  // coverage chunks the segmenter couldn't attribute, and/or flagged (never auto-merged) duplicate suspects. null =
  // no exceptions on this parse (the fast paths — evaluate auto-adopt, craft's plain confirm screen — are unchanged).
  const [importExceptions, setImportExceptions] = useState<{
    contactCandidate: ContactCandidate | null; chunks: CoverageChunk[]; duplicateSuspects: DuplicateSuspect[]; sourceText: string;
  } | null>(null);
  // EVALUATE-only: when exceptions gate the auto-adopt, the commit args wait here for the panel's Continue click.
  const [pendingEvalCommit, setPendingEvalCommit] = useState<{ specialtyId: string; entries: CommitEntry[]; stream?: string } | null>(null);
  // EVALUATE flow only — specialty stated UP FRONT (before parse), so the wait is legible. pendingText holds the CV
  // text between ImportCv and the specialty step; evalSpecialtyId is the chosen lens, threaded into commit (STATE-only).
  const [pendingText, setPendingText] = useState<string | null>(null);
  const [evalSpecialtyId, setEvalSpecialtyId] = useState('');
  const [evalStream, setEvalStream] = useState(''); // 'img' | 'cmg' — captured in EvalSetup, persisted on the CV, gates the IMG panel
  const [importSourceFilename, setImportSourceFilename] = useState(''); // B3: the uploaded file's name → names the new cv
  const [cvList, setCvList] = useState<CvSummary[] | null>(null);
  const [dismissedPicker, setDismissedPicker] = useState(false);
  // Magic-link sign-in (production auth UI).
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  // Magic-link confirm: the token stashed from /login/verify?token=… — verified on the user's CLICK,
  // never on mount (so an email-scanner prefetch can't burn the single-use token).
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false); // unauth: false = marketing landing, true = the sign-in form

  const section = sections.find((s) => s.id === sectionId) || null;
  const archetype = section?.archetype ?? 'experience';
  const isCredential = archetype === 'credential';

  // Auth bootstrap. If we landed on the magic-link confirm route (/login/verify?token=…), STASH the token and
  // show a click-to-confirm screen — do NOT POST on mount. The token is consumed only on the user's click
  // (confirmSignIn), so an email-scanner prefetch that merely loads this page can't burn the single-use token.
  // Otherwise just check the existing session.
  // Revenue P0 (P0-1) — the LemonSqueezy redirect_url lands here after checkout. Detected on boot (before the
  // auth check resolves) so the confirming-your-unlock view can render as soon as `authed` is known — the checkout
  // session is a real signed-in session (checkout requires auth), so this is never reached logged-out in practice,
  // but the view itself doesn't assume `authed` — it waits for the normal bootstrap below.
  const [onCheckoutSuccessRoute, setOnCheckoutSuccessRoute] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.pathname === '/checkout/success') {
      setOnCheckoutSuccessRoute(true);
      window.history.replaceState({}, '', '/'); // drop the route from the address bar; the view state carries it now
    }
    if (url.pathname === '/login/verify') {
      const token = url.searchParams.get('token') ?? '';
      window.history.replaceState({}, '', '/'); // drop the token from the address bar
      if (token) { setPendingToken(token); setAuthed(false); return; } // confirm screen; no POST yet
    }
    api('/api/auth/me').then((r) => setAuthed(r.ok));
  }, []);

  // Fires the magic-link verify on the user's CLICK (not on mount).
  async function confirmSignIn() {
    if (!pendingToken) return;
    setVerifying(true); setError('');
    const r = await api('/api/auth/verify', { method: 'POST', body: JSON.stringify({ token: pendingToken }) });
    setVerifying(false);
    if (r.ok) { setPendingToken(null); setAuthed(true); }
    // On failure, reveal the sign-in form (not the landing) so the error message is visible.
    else { setPendingToken(null); setAuthed(false); setShowSignIn(true); setError('That sign-in link is invalid or expired — request a new one.'); }
  }
  useEffect(() => {
    if (!authed) return;
    api<Me>('/api/auth/me').then((r) => { if (r.ok) setMe(r.data); }); // capture identity (email etc.), not just r.ok
    api<{ specialties: Specialty[] }>('/api/specialties').then((r) => setSpecialties(r.data?.specialties ?? []));
    api<{ sections: Section[] }>('/api/sections').then((r) => setSections(r.data?.sections ?? []));
    api<{ profile: { name: string | null; phone: string | null; city: string | null } }>('/api/profile').then((r) => {
      const p = r.data?.profile; if (p) setContact({ name: p.name ?? '', phone: p.phone ?? '', city: p.city ?? '' });
    });

    // B2 fix — a boot-path public route (/privacy /terms /refunds /help) must survive the post-auth effect: an
    // AUTHED user opening one of these (footer link, bookmark, direct URL) should just see the page, not get
    // clobbered onto checkout-success / door-intent / the single-cv auto-restore below. Data loads above still
    // run (profile/specialties/etc. are harmless to prefetch); every setView() below this guard is skipped.
    if (bootedPublicRoute.current) {
      api<{ cvs: CvSummary[] }>('/api/cvs').then((r) => setCvList(r.data?.cvs ?? []));
      return;
    }

    // Revenue P0 (P0-1) — the checkout-success route takes priority over every other post-auth routing below
    // (door intent, the single-cv auto-restore): it's an explicit return trip from a real payment.
    if (onCheckoutSuccessRoute) { setOnCheckoutSuccessRoute(false); setView('checkout-success'); return; }

    // Revenue P0 (P0-1) — door intent: a logged-out click on Craft/Evaluate stashed which door before showing the
    // sign-in form; once auth completes (magic-link verify OR dev-login/session-restore), route straight there
    // instead of dropping the user on the generic authoring view. One-shot (cleared immediately).
    let door: string | null = null;
    try { door = window.localStorage.getItem('cvcraft_door'); if (door) window.localStorage.removeItem('cvcraft_door'); } catch { /* best-effort */ }
    if (door === 'craft') { setView('craft'); }
    else if (door === 'evaluate') { enterImport('evaluate'); }

    // Phase 6 — restore on reload: 0 → fresh-start; 1 → auto-restore (review if populated); >1 → picker.
    // Skipped when a door intent just routed us somewhere explicit — don't let the auto-restore clobber it.
    api<{ cvs: CvSummary[] }>('/api/cvs').then((r) => {
      const list = r.data?.cvs ?? [];
      setCvList(list);
      if (!door && list.length === 1) {
        setCvId(list[0].id); setSpecialtyId(list[0].specialty_id);
        if (list[0].entry_count > 0) setView('review');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  function resumeCv(cv: CvSummary) {
    setCvId(cv.id); setSpecialtyId(cv.specialty_id); setDismissedPicker(true);
    if (cv.entry_count > 0) setView('review');
  }

  // Phase B1 — open a populated cv's STORED quality read cross-session ("Review"). Routes to the read view and loads
  // the deterministic coverage WITHOUT re-running the LLM tagger (tags already exist from the original import — no new
  // LLM call on open). QualityReadView then reuses the stored quality read (Phase A determinism).
  function reviewCv(cv: CvSummary) {
    setCvId(cv.id); setSpecialtyId(cv.specialty_id); setDismissedPicker(true);
    setParseDropped(0); // C3a — a stored-read open isn't a fresh parse; don't carry a stale note from a prior import
    // …and, for the same reason, it is not a fresh import: opening a stored CV must never begin a paywalled
    // extraction on the applicant's behalf (§3's journey is entered from the IMPORT flow, not from the picker).
    setFreshImport(false);
    setView('evaluate-read'); void openStoredRead(cv.id);
  }

  // Header "CVCraft" / "Your CVs" — return to the CV picker (the trap fix: from inside a CV there was no way
  // back without a reload). Clears the active CV and re-shows the picker; does NOT clear the in-progress entry
  // form, so an unsaved entry isn't lost. Refetch the list so the picker reflects current state.
  function goToPicker() {
    setView('author'); setCvId(''); setDismissedPicker(false); setError('');
    api<{ cvs: CvSummary[] }>('/api/cvs').then((r) => setCvList(r.data?.cvs ?? []));
  }

  // The hub (landing) is reachable while signed in — the wordmark goes here, NOT to app-home. From the hub the
  // user switches between the Craft and Evaluate apps (mirrors PSCraft's wordmark→landing).
  function goToHub() { setView('hub'); setError(''); }

  // Craft door → "start fresh" lands on blank authoring. Clear the active CV + dismiss the picker so a fresh
  // authoring surface renders (not a resumed CV, not the multi-CV picker).
  function startFresh() { setCvId(''); setSpecialtyId(''); setCraftStream(''); setDismissedPicker(true); setError(''); setView('author'); }

  // Conversational Phase 0 — "Start a new CV" opens the guided interview (name → city → phone → specialty → stream),
  // NOT the blank form. Same fresh-CV clears as startFresh; the interview hands specialty+stream to authoring.
  function startGuided() { setCvId(''); setSpecialtyId(''); setCraftStream(''); setDismissedPicker(true); setError(''); setView('guided'); }

  // Close the read→act loop: a coverage gap → land in authoring with the gap's section preselected + a banner.
  // RECOVER framing (the read's intent): add experience you already HAVE that isn't listed — not "go acquire it".
  function onAddForGap(dimensionId: string) {
    const m = DIM_SECTION[dimensionId];
    if (m?.section) setSectionId(m.section); // preselect the section so the user lands ready to add
    setGapPrompt(m?.phrase ?? '');
    setError(''); setView('author');
  }

  // Enter the SHARED import flow in a given mode. 'craft' (import → keep building) vs 'evaluate' (import → read).
  function enterImport(mode: 'craft' | 'evaluate') {
    setImportMode(mode); setImportedText(''); setCandidates(null); setEvalRead(null); setError('');
    setPendingText(null); setEvalSpecialtyId(''); setEvalStream(''); setParseDropped(0); setView('import');
    setImportExceptions(null); setPendingEvalCommit(null);
  }

  // P4a evaluate post-commit: run the existing tagger over the imported entries, then read coverage. Writes only
  // entry_dimensions (the existing tags writer) — never CV text. The read is observations + questions.
  const TAG_STAGES = ['Reading your entries…', 'Identifying what each one shows…', 'Weighing it up against this area…'];
  async function runEvaluateRead(cvId: string) {
    setEvalBusy(true); setEvalRead(null); setTagsIncomplete(false);
    const inferR = await api(`/api/tags/cv/${cvId}/infer`, { method: 'POST' }); // LLM tagger → entry_dimensions
    if (!inferR.ok) setTagsIncomplete(true); // C2: transactional infer failed — existing tags kept, but this pass didn't add any
    const r = await api<{ read: CoverageRead }>(`/api/coverage/cv/${cvId}`); // deterministic, de-frameworked, hedged
    setEvalRead(r.ok ? r.data.read : { hasContent: false, lead: '', recover: [] });
    setEvalBusy(false);
  }

  // Phase B1 — open a STORED cv's read WITHOUT the tagger (tags already exist; no new LLM call). Fetches only the
  // deterministic coverage read; QualityReadView reuses the persisted quality read. Used by "Review"/"View evaluation".
  async function openStoredRead(cvId: string) {
    setEvalBusy(true); setEvalRead(null); setEvalStream('');
    const r = await api<{ read: CoverageRead }>(`/api/coverage/cv/${cvId}`);
    setEvalRead(r.ok ? r.data.read : { hasContent: false, lead: '', recover: [] });
    setEvalBusy(false);
  }

  // ImportCv confirmed the text. CRAFT parses immediately (specialty is chosen later, at confirm). EVALUATE holds the
  // text and prompts for specialty FIRST (so the wait can name it) — parse fires from confirmEvalSpecialty.
  function onImportReady(text: string, fileName?: string) {
    setImportSourceFilename(fileName ?? ''); // B3: remember the source filename to name the new cv
    if (importMode === 'evaluate') setPendingText(text);
    else void parseImport(text);
  }

  // EVALUATE — specialty + stream chosen up front → remember them (wait copy + IMG panel) and parse. The fresh
  // values are ALSO passed to parseImport explicitly: setState is async, so the auto-adopt must NOT read them back
  // from (stale) closure state this same tick — it would commit an empty specialty_id.
  function confirmEvalSpecialty(specialtyId: string, stream: string) {
    const t = pendingText; if (!t) return;
    setEvalSpecialtyId(specialtyId); setEvalStream(stream); setPendingText(null);
    void parseImport(t, { specialtyId, stream });
  }

  // ImportCv confirmed the text → parse into candidates (P2a /api/parse). Read-only; no write yet.
  // evalCtx carries the FRESH specialty+stream for the evaluate auto-adopt (avoids the stale-closure trap — see
  // confirmEvalSpecialty). Craft passes nothing (it shows the confirm screen, no auto-adopt).
  async function parseImport(text: string, evalCtx?: { specialtyId: string; stream: string }) {
    setImportedText(text); setCandidates(null); setImportBusy(true); setError(''); setImportExceptions(null); setPendingEvalCommit(null);
    const r = await api<{
      candidates: Candidate[]; dropped?: number; error?: string;
      contact_candidate?: ContactCandidate | null; source_coverage?: { covered_ratio: number; chunks: CoverageChunk[] };
      duplicate_suspects?: DuplicateSuspect[];
    }>('/api/parse', { method: 'POST', body: JSON.stringify({ text }) });
    if (r.ok) {
      const cands = r.data?.candidates ?? [];
      // C3a — EVALUATE only: surface the honest drop count on the read view (non-blocking; the flow keeps running).
      if (importMode === 'evaluate') setParseDropped(r.data?.dropped ?? 0);

      // G1-1c — SILENT-LOSS exceptions from this parse: a contact fact the CURRENT profile lacks, unattributed
      // source chunks, and/or flagged duplicate suspects. Computed against the profile already loaded into `contact`
      // (fetched at auth bootstrap) — never against a re-fetch, so this stays a pure client-side gate.
      const contactCandidate = r.data?.contact_candidate ?? null;
      const missingFields = { name: !contact.name.trim(), phone: !contact.phone.trim(), city: !contact.city.trim() };
      const contactHasNew = !!contactCandidate && (['name', 'phone', 'city'] as const).some((f) => contactCandidate[f] && missingFields[f]);
      const chunks = r.data?.source_coverage?.chunks ?? [];
      const duplicateSuspects = r.data?.duplicate_suspects ?? [];
      const hasExceptions = contactHasNew || chunks.length > 0 || duplicateSuspects.length > 0;
      if (hasExceptions) setImportExceptions({ contactCandidate, chunks, duplicateSuspects, sourceText: text });

      // EVALUATE: no confirm screen — auto-adopt the parsed entries (the user's OWN verbatim CV text) then read.
      // A blank section (e.g. "Professional Highlights", no taxonomy match) → the neutral "Additional Information"
      // bucket so nothing is dropped. Coverage is section-independent; the user can re-sort in Craft. The spinner
      // stays up through the commit (importBusy true, candidates null) — commitImport routes evaluate → the read.
      if (importMode === 'evaluate' && cands.length > 0) {
        const entries = cands.map((c) => ({
          section: c.section || 'additional-information', title: c.title, organization: c.organization,
          start_date: c.start_date, end_date: c.end_date, date_text: c.date_text, bullet: c.descriptive,
        }));
        const specialtyId = evalCtx?.specialtyId ?? evalSpecialtyId;
        const stream = evalCtx?.stream ?? evalStream; // FRESH specialty+stream (not stale state); stream persists on CV creation
        // With exceptions: HOLD the auto-adopt behind the pre-read panel's Continue click. No exceptions → unchanged.
        if (hasExceptions) { setImportBusy(false); setPendingEvalCommit({ specialtyId, entries, stream }); return; }
        await commitImport(specialtyId, entries, stream);
        return;
      }
      setImportBusy(false); setCandidates(cands); // craft → confirm screen (exceptions panel renders above it); empty → scramble panel
      return;
    }
    setImportBusy(false);
    // Each non-200 gets an HONEST message keyed on the failure code (rides in r.data.error for every parse failure):
    // 422 too-long / 413 too-large → import-in-sections; 502 timeout → one-section-at-a-time; 503 → busy-try-later;
    // parse_failed / network / unknown → the generic retry. (No more "paste a cleaner copy" for a CV that's fine.)
    setError(parseErrorMessage((r.data as { error?: string } | null)?.error));
  }

  // P3 — COMMIT the confirmed candidates via the EXISTING writer (POST /api/entries), one per included entry.
  // Bullets are the user-edited box content (confirm-and-adopt); an empty box commits no bullet. No new write path.
  async function commitImport(specialtyId: string, entries: CommitEntry[], stream?: string) {
    setImportBusy(true); setError('');
    // B3 — ONE batched request that INSERTS A NEW CV (no cv_id sent → distinct cv per import; replace is RETIRED). Each
    // evaluate or craft import is its own stored cv — no merge, no collision. source_filename names the new cv.
    const r = await api<{ cv_id: string; committed: number }>('/api/entries/batch', { method: 'POST', body: JSON.stringify({
      specialty_id: specialtyId,
      ...(stream ? { stream } : {}), // set on CV creation
      ...(importSourceFilename ? { source_filename: importSourceFilename } : {}),
      entries: entries.map((e) => ({
        section: e.section, title: e.title, organization: e.organization,
        start_date: e.start_date, end_date: e.end_date, date_text: e.date_text,
        // confirm-and-adopt: only the user's text, only if kept. ATOMIC BULLETS — send the parsed `descriptive` as ONE
        // raw block for BOTH modes; the SERVER splits it on the "\n\n" LIST-ITEM boundary (splitListItems) into one
        // stored bullet per source list item. This replaces the old client-side `splitPoints` (a SENTENCE-level split)
        // that OVER-split the evaluate funnel — a multi-sentence accomplishment was fragmented into several point-
        // cards. Per Sol's ruling, atomic = one list item, not one sentence: a multi-sentence point now stays one row.
        bullets: !e.bullet.trim() ? [] : [e.bullet.trim()],
      })),
    }) });
    setImportBusy(false);
    const lastCv = r.ok ? (r.data?.cv_id ?? '') : '';
    if (!lastCv) { setError('Couldn’t add those entries — please try again.'); return; }
    const ok = r.data?.committed ?? entries.length;
    if (ok < entries.length) setError(`Added ${ok} of ${entries.length} entries — add the rest in authoring.`);
    // The imported CV is now entries in-app. Common: activate it + refresh the list.
    setCvId(lastCv); setSpecialtyId(specialtyId); setImportedText(''); setCandidates(null); setDismissedPicker(true);
    api<{ cvs: CvSummary[] }>('/api/cvs').then((rr) => setCvList(rr.data?.cvs ?? []));
    // DIVERGE on mode: craft → the conversational refine (Phase 3 — walk the parsed entries together);
    // evaluate → the read (tag → coverage → render).
    if (importMode === 'evaluate') { setFreshImport(true); setView('evaluate-read'); void runEvaluateRead(lastCv); }
    else { setGuidedMode('refine'); setView('guided-author'); }
  }

  // G1-1c — the exceptions panel's contact-confirm: fill-MISSING-only (the panel only ever offers fields the
  // current profile lacks, and only the ones the user checked are sent) — never overwrites a populated field.
  async function confirmContactCandidate(fields: Partial<ContactCandidate>) {
    if (Object.keys(fields).length === 0) return;
    await api('/api/profile', { method: 'PATCH', body: JSON.stringify(fields) });
    setContact((c) => ({ ...c, ...fields }));
  }
  // EVALUATE: the panel's Continue releases the held auto-adopt commit.
  function continueEvalImportExceptions() {
    setImportExceptions(null);
    const pending = pendingEvalCommit; setPendingEvalCommit(null);
    if (pending) void commitImport(pending.specialtyId, pending.entries, pending.stream);
  }
  // CRAFT: no flow change — the panel is informational, shown above the existing confirm list; Continue just dismisses it.
  function dismissCraftImportExceptions() { setImportExceptions(null); }

  // Shared sign-out reset (landing + drop per-session view/identity state).
  function resetToSignedOut() {
    setAuthed(false); setShowSignIn(false); setLinkSent(false); setEmail(''); setMe(null);
    setCvId(''); setView('author'); setDismissedPicker(false); setCvList(null);
  }

  async function signOut() {
    await api('/api/auth/logout', { method: 'POST' });
    resetToSignedOut();
  }

  // Delete the account via the existing DELETE /api/auth/account (soft-delete + 7-day grace; the server purges
  // sessions + clears the cookie). On success, drop to the signed-out landing. Returns ok so the Account view can
  // surface a retry on failure. NO new server logic — this only calls the endpoint and resets client state.
  async function deleteAccount(): Promise<boolean> {
    const r = await api('/api/auth/account', { method: 'DELETE' });
    if (r.ok) resetToSignedOut();
    return r.ok;
  }

  // Thin, calm header for the AUTHENTICATED app views (authoring / picker / review / craft / evaluate / account)
  // — NOT the hub (the Landing has its own header). Mirrors PSCraft: the wordmark goes to the HUB (the landing),
  // and a SEPARATE always-visible "Your CVs" goes to app-home (the picker). Net: CVCraft(→hub) · {email}(→account)
  // · Your CVs(→app-home) · Sign out. Restrained register (Principle 5): a masthead, not a nav bar.
  const appHeader = (
    // Mobile (≤480px): tighter padding + a compact "Account" label instead of the long email, so the wordmark + nav
    // fit on ONE line at 375px (the email's maxWidth:220 was the overflow culprit). Desktop is unchanged.
    <header style={narrow ? { ...S.appHeader, padding: '0 var(--space-3)' } : S.appHeader} data-testid="app-header">
      <button style={S.brandBtn} onClick={goToHub} data-testid="app-home">CVCraft</button>
      <span style={S.headerActions}>
        {me && (
          <button className="btn btn-ghost" style={narrow ? S.headerBtn : S.headerEmail} onClick={() => setView('account')} title={me.email} data-testid="nav-account">{narrow ? 'Account' : me.email}</button>
        )}
        <button className="btn btn-ghost" style={S.headerBtn} onClick={goToPicker} data-testid="nav-your-cvs">Your CVs</button>
        <button className="btn btn-ghost" style={S.headerBtn} onClick={signOut} data-testid="nav-signout">Sign out</button>
      </span>
    </header>
  );

  // Revenue P0 (P0-1) — /checkout/success: bounded polling for the webhook's fulfillment, then restore the user
  // to where they were. 'polling' | 'confirmed' (brief toast, then restore) | 'timeout' (manual-recovery panel).
  const [checkoutPhase, setCheckoutPhase] = useState<'polling' | 'confirmed' | 'timeout'>('polling');
  const [checkoutTry, setCheckoutTry] = useState(0);
  const CHECKOUT_MAX_TRIES = 40;
  const CHECKOUT_INTERVAL_MS = 3000;
  const checkoutCtxRef = useRef<checkoutContext.CheckoutContext | null>(null);
  // Sol's rule — never change a buyer's balance silently. Set when the server COMPENSATED a credit/single
  // collision (a single order landed on a CV a bundle credit had already paid for, so the credit was returned).
  const [creditReturned, setCreditReturned] = useState(false);

  function restoreCheckoutContext() {
    const ctx = checkoutCtxRef.current;
    checkoutContext.clear();
    if (!ctx) { goToPicker(); return; }
    setCvId(ctx.cvId);
    if (ctx.view === 'guided-author' && ctx.guidedMode) setGuidedMode(ctx.guidedMode);
    setReadReloadKey((k) => k + 1); // re-drives a paused write (GuidedAuthor's pendingSave retry) / re-fires the read
    setView((ctx.view as typeof view) || 'author');
    // Hotfix (live §F smoke, 2026-08-22): the coverage read is fetched imperatively (openStoredRead), not by the
    // reloadKey — restoring straight into 'evaluate-read' without it left evalRead null and the view parked on the
    // ParsingIndicator forever. Mirror the "View your evaluation" path.
    if (ctx.view === 'evaluate-read' && ctx.cvId) { setFreshImport(false); void openStoredRead(ctx.cvId); }
  }

  useEffect(() => {
    if (view !== 'checkout-success' || checkoutPhase !== 'polling') return;
    let cancelled = false;
    if (checkoutTry === 0) checkoutCtxRef.current = checkoutContext.load();
    const ctx = checkoutCtxRef.current;
    if (!ctx) { setCheckoutPhase('timeout'); return; } // nothing to confirm against — straight to manual recovery

    (async () => {
      const meR = await api<Me>('/api/auth/me');
      if (cancelled) return;
      const creditsNow = meR.ok ? (meR.data.bundle_credits_remaining ?? 0) : 0;
      const cvsR = await api<{ cvs: CvSummary[] }>('/api/cvs');
      if (cancelled) return;
      const targetCv = (cvsR.data?.cvs ?? []).find((c) => c.id === ctx.cvId);
      if (targetCv?.is_paid) {
        // A single-tier purchase can collide with a credit that already covered this CV; the server compensates by
        // returning the credit. Ask whether that happened so the confirmation can SAY so (never a silent balance
        // change). Best-effort — a failed check must never block the unlock the buyer already paid for.
        if (ctx.tier === 'single') {
          const ent = await api<{ credit_returned?: boolean }>(`/api/lemonsqueezy/entitlement?cv_id=${encodeURIComponent(ctx.cvId)}`);
          if (cancelled) return;
          if (ent.ok && ent.data.credit_returned) setCreditReturned(true);
        }
        setCheckoutPhase('confirmed'); return; // single tier fulfilled (or already redeemed)
      }

      // A bundle purchase adds credits but doesn't touch THIS cv directly — redeem one against the intended cv
      // as soon as any credit is available. (The previous baseline-delta wait raced the webhook: fulfillment
      // usually lands BEFORE the first poll, so the already-granted credits became the baseline and the delta
      // never fired — the buyer span 2 minutes into "Still confirming". Owner-hit live, 2026-08-04.)
      //
      // MONEY GUARD: NOT when this checkout was a SINGLE for THIS cv. That order pays for this CV directly, so
      // spending a bundle credit here would charge the buyer twice for one CV (~CA$19 + a ~CA$16 credit) and the
      // single's own fulfillment would then silently no-op on `AND paid_at IS NULL`. A single waits for its own
      // fulfillment and otherwise falls through to the timeout/Retry panel, exactly as before. cycle/bundle — and
      // LEGACY contexts saved before `tier` existed — keep the auto-redeem (the 2026-08-04 race fix).
      if (ctx.tier !== 'single' && creditsNow > 0) {
        const redeemR = await api<{ ok?: boolean }>('/api/lemonsqueezy/redeem', { method: 'POST', body: JSON.stringify({ cv_id: ctx.cvId }) });
        if (cancelled) return;
        if (redeemR.ok) { setCheckoutPhase('confirmed'); return; }
      }

      if (checkoutTry + 1 >= CHECKOUT_MAX_TRIES) { setCheckoutPhase('timeout'); return; }
      setTimeout(() => { if (!cancelled) setCheckoutTry((n) => n + 1); }, CHECKOUT_INTERVAL_MS);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, checkoutPhase, checkoutTry]);

  // The brief confirmation toast, then restore to where the user was. HELD when a credit was returned — a balance
  // change the buyer must actually read, and a 1.2s toast is not read. They continue on their own click instead.
  useEffect(() => {
    if (checkoutPhase !== 'confirmed' || creditReturned) return;
    const t = setTimeout(() => restoreCheckoutContext(), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutPhase, creditReturned]);

  function retryCheckoutPoll() { setCheckoutTry(0); setCheckoutPhase('polling'); }

  // Payment Phase 2/3 — the paywall overlay (rendered in every view a 402 can originate from). On unlock-via-credit:
  // close, clear errors, and refetch the library so the now-paid CV reflects; the user re-tries the action.
  const paywall = paywallCvId ? (
    <PaywallModal
      cvId={paywallCvId}
      onClose={() => setPaywallCvId(null)}
      onUnlocked={() => { setPaywallCvId(null); setError(''); setReadReloadKey((k) => k + 1); void api<{ cvs: CvSummary[] }>('/api/cvs').then((r) => setCvList(r.data?.cvs ?? [])); }}
      // Revenue P0 (P0-1) — where to restore to on the /checkout/success return trip (window.location redirect
      // to LemonSqueezy would otherwise strand a mid-conversation draft).
      checkoutReturn={{ view, guidedMode }}
    />
  ) : null;

  async function downloadCv() {
    setError('');
    await api('/api/profile', { method: 'PATCH', body: JSON.stringify(contact) }); // save the contact header first
    const q = addCrafted ? '?crafted=1' : ''; // Phase 10: opt-in "Crafted [date]" stamp
    const res = await fetch(`/api/cvs/${cvId}/export.${exportFormat}${q}`, { credentials: 'include' });
    if (res.status === 402) { setPaywallCvId(cvId); return; } // export on an unpaid CV → paywall
    if (!res.ok) { setError(`export failed (${res.status})`); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `cv.${exportFormat}`; document.body.appendChild(link); link.click();
    link.remove(); URL.revokeObjectURL(url);
  }

  async function devLogin() {
    setError('');
    const r = await api('/api/auth/dev-login', { method: 'POST', body: JSON.stringify({ email: 'author@local.test', token: 'cvcraft-local-dev-token' }) });
    if (r.ok) setAuthed(true); else setError('dev-login failed (local only)');
  }

  // Production sign-in: request a one-time magic link to the user's email (POST /api/auth/request).
  async function requestLink() {
    if (!email.trim()) return;
    setError(''); setSending(true);
    const r = await api('/api/auth/request', { method: 'POST', body: JSON.stringify({ email: email.trim() }) });
    setSending(false);
    if (r.ok) { setLinkSent(true); return; }
    // Surface the server's own message (e.g. the 429 "Too many attempts — please wait a minute" rate-limit, or
    // "That doesn't look like a valid email") so a rate-limited user isn't wrongly told their address is bad.
    // Fall back to the generic line only when the envelope carried no user_message.
    const msg = (r.data as { user_message?: string } | null)?.user_message;
    setError(msg ?? "Couldn't send the sign-in link — check the email address and try again.");
  }

  async function checkBullet() {
    setChecking(true); setError(''); setResult(null);
    const r = await api<{ result: ClassifyResult }>('/api/classify', { method: 'POST', body: JSON.stringify({ bullet_text: draft, section: sectionId, archetype }) });
    setChecking(false);
    if (r.ok) setResult(r.data.result); else setError(`classify failed (${r.status})`);
  }
  function addBullet() { if (draft.trim()) { setBullets((b) => [...b, draft.trim()]); setDraft(''); setResult(null); } }

  // 9b — voice: speak to fill the describe-box. MediaRecorder captures audio/webm; the blob POSTs to
  // /api/transcribe (Cloudflare Whisper) and the RAW transcript fills the editable describe-box — the user
  // confirms/fixes it, then runs the existing 9a extract. No cleanup; the audio is transcribe-and-discard.
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const [micError, setMicError] = useState('');
  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function stopRecTimers() {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    mediaRef.current?.stream.getTracks().forEach((t) => t.stop());
  }

  function stopRecording() {
    if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop();
    setRecording(false);
  }

  async function startRecording() {
    setMicError('');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setMicError('Microphone access needed — allow it in your browser, or just type in the box.');
      return;
    }
    const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = async () => {
      stopRecTimers();
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      if (blob.size === 0) return;
      if (blob.size > MAX_AUDIO_BYTES) { setMicError('That recording is too large to transcribe — try a shorter take.'); return; }
      setTranscribing(true);
      try {
        const res = await fetch('/api/transcribe', { method: 'POST', credentials: 'include', body: blob });
        if (!res.ok) { setMicError('Transcription failed — try again, or type in the box.'); }
        else {
          const { text } = await res.json() as { text: string };
          // RAW transcript fills the EDITABLE box (append to anything already there); the user confirms/fixes.
          if (text) setDescribe((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
        }
      } catch { setMicError('Transcription failed — try again, or type in the box.'); }
      setTranscribing(false);
    };
    mediaRef.current = rec;
    rec.start();
    setRecording(true);
    setRecSeconds(0);
    timerRef.current = setInterval(() => setRecSeconds((s) => s + 1), 1000);
    autoStopRef.current = setTimeout(() => stopRecording(), MAX_REC_SECONDS * 1000);
  }

  // 9a — describe an entry in plain language; the read PRE-FILLS the editable form (facts only). Nothing is
  // saved here — the user edits/confirms below, then Save creates the entry via the existing path. Descriptive
  // content pre-fills the bullet box with the user's OWN words; the user authors the bullet there.
  interface Proposal { section: string; title: string; organization: string; start_date: string; end_date: string; date_text: string; descriptive: string }
  async function runExtract() {
    if (!describe.trim()) return;
    setExtracting(true); setError('');
    const r = await api<{ extracted: boolean; proposal?: Proposal }>('/api/extract', { method: 'POST', body: JSON.stringify({ text: describe.trim(), specialty_id: specialtyId }) });
    setExtracting(false);
    // No usable facts / parse fail → degrade to the manual form (never a hollow pre-fill).
    if (!r.ok || !r.data.extracted || !r.data.proposal) { setError("Couldn't pull entry details from that — add them directly in the fields below."); return; }
    const p = r.data.proposal;
    if (p.section) setSectionId(p.section);
    setTitle(p.title); setOrganization(p.organization);
    setStartDate(p.start_date); setEndDate(p.end_date); setDateText(p.date_text);
    if (p.descriptive) setDraft(p.descriptive); // user's verbatim words → bullet box; the user authors it
    setResult(null);
  }

  async function saveEntry() {
    setError('');
    const all = draft.trim() ? [...bullets, draft.trim()] : bullets;
    const r = await api<{ entry_id: string; cv_id: string }>('/api/entries', {
      method: 'POST',
      // B3: append to the ACTIVE cv (cv_id) so authoring builds ONE cv; no cv_id (fresh authoring) → the server mints a new
      // cv — and the guided interview's stream rides along on that creation (set-on-creation-only, matching the server).
      body: JSON.stringify({ specialty_id: specialtyId, section: sectionId, title, organization, start_date: startDate, end_date: endDate, date_text: dateText, bullets: all, ...(cvId ? { cv_id: cvId } : craftStream ? { stream: craftStream } : {}) }),
    });
    if (maybePaywall(r)) return; // craft on an unpaid CV → paywall (the 402 carries the cv_id to unlock)
    if (!r.ok) { setError(`save failed (${r.status})`); return; }
    if (r.data?.cv_id) setCvId(r.data.cv_id);
    setSaved((s) => [...s, { section: sectionId, sectionName: section?.name ?? sectionId, archetype, title, organization, bullets: all }]);
    // reset for the next entry; keep the specialty (one CV).
    setSectionId(''); setTitle(''); setOrganization(''); setStartDate(''); setEndDate(''); setDateText(''); setDescribe(''); setBullets([]); setDraft(''); setResult(null); setGapPrompt('');
  }

  // Trust/Legal P1 (+ Polish B2's /refunds) — these render BEFORE the auth gate: publicly readable by a
  // signed-out visitor (e.g. the Landing footer links), so they cannot wait on `authed` to resolve.
  if (view === 'privacy' || view === 'terms' || view === 'refunds') return <LegalPage doc={view} onBack={() => { setView('author'); window.history.replaceState({}, '', '/'); }} />;
  // Polish B2 — /help, same publicly-readable boot-path treatment.
  if (view === 'help') return <Help onBack={() => { setView('author'); window.history.replaceState({}, '', '/'); }} />;

  if (authed === null) return <main style={S.main}><h1>CVCraft</h1><p>Loading…</p></main>;
  // Magic-link confirm screen — click-to-confirm (the verify POST fires on the button, not on mount).
  // MUST precede the landing: a clicked magic link is mid-login, so show the confirm screen, never the marketing page.
  if (!authed && pendingToken) return (
    <main style={S.main}>
      <h1>CVCraft</h1>
      <p>Confirm sign-in to finish logging in on this device.</p>
      <button className="btn btn-primary" onClick={confirmSignIn} disabled={verifying} data-testid="verify-confirm-btn">{verifying ? 'Signing in…' : 'Sign in'}</button>
      {error && <p style={S.err}>{error}</p>}
    </main>
  );
  // Unauthenticated, not mid-login: the landing hub sits in front; logged-out, every door funnels to sign-in.
  // Revenue P0 (P0-1) — door intent: stash WHICH door the logged-out user clicked before showing sign-in, so a
  // successful magic-link/dev-login lands them in Craft/Evaluate instead of the generic post-auth default.
  // Landing passes the door through its onSignIn callback (both doors funnel to sign-in when logged out).
  if (!authed && !showSignIn) return (
    <Landing
      authed={false}
      onCraft={() => setShowSignIn(true)}
      onEvaluate={() => setShowSignIn(true)}
      onWorkspace={() => setShowSignIn(true)}
      onSignIn={(door) => {
        if (door) { try { window.localStorage.setItem('cvcraft_door', door); } catch { /* best-effort */ } }
        setShowSignIn(true);
      }}
    />
  );
  if (!authed) return (
    <main style={S.main}>
      <button className="btn btn-ghost" style={{ marginLeft: 0, fontSize: 'var(--fs-small)' }} onClick={() => { setShowSignIn(false); setError(''); }} data-testid="back-to-landing"><IconBack aria-hidden="true" /> Back</button>
      <h1>CVCraft</h1>
      {linkSent ? (
        <p data-testid="link-sent">Check your email — we sent a one-time sign-in link to <strong>{email}</strong>. It expires in 15 minutes. You can close this tab and open the link.</p>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); requestLink(); }}>
          <p>Sign in — enter your email and we'll send you a one-time sign-in link.</p>
          <label style={S.label}>Email
            <input style={S.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" data-testid="email" />
          </label>
          <button className="btn btn-primary" type="submit" disabled={sending || !email.trim()} data-testid="request-link">{sending ? 'Sending…' : 'Email me a sign-in link'}</button>
        </form>
      )}
      {/* dev-login is a local convenience only — it 404s in production, so only surface it in dev builds. */}
      {import.meta.env.DEV && <button className="btn btn-secondary" style={{ marginTop: 'var(--space-3)' }} onClick={devLogin} data-testid="dev-login">Sign in (dev)</button>}
      {error && <p style={S.err}>{error}</p>}
    </main>
  );

  // The HUB (signed in) — the same auth-aware Landing, reachable via the wordmark, to switch between the two apps.
  // Renders WITHOUT appHeader: the Landing's own header (wordmark + "Go to your CVs") is the hub's chrome.
  if (view === 'hub') return (
    <Landing
      authed
      onCraft={() => setView('craft')}        // Craft door → start-fresh/import choice (the craft app)
      onEvaluate={() => enterImport('evaluate')}  // Evaluate door → shared import in EVALUATE mode → the read
      onWorkspace={goToPicker}                 // "Go to your CVs" → app-home (picker/authoring)
      onSignIn={() => {}}                       // unused when authed
    />
  );

  // Revenue P0 (P0-1) — the LemonSqueezy return trip. Bounded poll (up to 40 tries × 3s ≈ 2 minutes) for the
  // webhook's fulfillment, then restore the user to where they were (the saved checkoutContext). A bundle
  // purchase auto-redeems one credit against the intended cv once credits appear (paid_at-only polling would
  // never complete for a cycle purchase, since a bundle purchase never sets THIS cv's paid_at by itself).
  if (view === 'checkout-success') return (
    <main style={S.main} data-testid="checkout-success">
      {checkoutPhase === 'polling' && (
        <>
          <h1>Confirming your unlock…</h1>
          <p style={{ color: 'var(--muted)' }}>This usually takes a few seconds.</p>
        </>
      )}
      {checkoutPhase === 'confirmed' && (
        <>
          <h1 data-testid="checkout-confirmed">You're unlocked!</h1>
          {creditReturned ? (
            <>
              {/* Sol: never change a buyer's balance silently. Calm and factual — this is good news (nothing was
                  lost), not an incident report. Shown where the buyer is already looking, and it HOLDS: the
                  auto-restore is suspended so they leave on their own click. */}
              <p data-testid="checkout-credit-returned">Your purchase covered this CV, so we returned 1 credit.</p>
              <button className="btn btn-primary" style={{ marginTop: 'var(--space-3)' }} onClick={restoreCheckoutContext} data-testid="checkout-continue">Continue</button>
            </>
          ) : (
            <p style={{ color: 'var(--muted)' }}>Taking you back…</p>
          )}
        </>
      )}
      {checkoutPhase === 'timeout' && (
        <>
          <h1>Still confirming</h1>
          <p>Payment received? It can take a minute — you can retry the check, or go back to your CV and we'll pick it up from there.</p>
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
            <button className="btn btn-primary" onClick={retryCheckoutPoll} data-testid="checkout-retry">Retry</button>
            <button className="btn btn-secondary" onClick={restoreCheckoutContext} data-testid="checkout-back">Back to your CV</button>
          </div>
        </>
      )}
    </main>
  );

  // CRAFT door — the craft app's entry: start fresh or bring in an existing CV (P1's craft-import, first-class).
  // NOT blank authoring (start-fresh routes there); NOT wired to the evaluate flow.
  if (view === 'craft') return (
    <>
      {appHeader}
      <main style={S.main} data-testid="craft-choice">
        <h1>Craft your CV</h1>
        <p>Start a new CV, or bring in one you already have.</p>
        <div>
          <button className="btn btn-primary" onClick={startGuided} data-testid="craft-start-fresh">Start a new CV</button>
          <button className="btn btn-secondary" onClick={() => enterImport('craft')} data-testid="craft-import">Import an existing CV</button>
        </div>
        {cvList && cvList.length > 0 && (
          <section data-testid="craft-resume" style={{ marginTop: 'var(--space-4)' }}>
            <h2 style={{ fontSize: 'var(--fs-h3)' }}>Or resume a CV you started</h2>
            <ul>
              {cvList.map((cv) => (
                <li key={cv.id} style={{ margin: 'var(--space-2) 0' }}>
                  <strong>{cv.specialty_name}</strong> — {cv.entry_count} {cv.entry_count === 1 ? 'entry' : 'entries'}{' '}
                  <button className="btn btn-ghost" style={{ fontSize: 'var(--fs-small)' }} onClick={() => resumeCv(cv)} data-testid={`craft-resume-${cv.id}`}>Resume</button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </>
  );

  // GUIDED START (Conversational Phase 0) — the interview opener: contact facts → specialty → stream, one question
  // per screen. onComplete: keep the contact header in state (the render/export path reads it), arm authoring with
  // the chosen specialty, and hold the stream for the first entry save (CV creation). Skip → the classic form.
  if (view === 'guided') return (
    <>
      {appHeader}
      <GuidedStart
        specialties={specialties}
        seed={contact}
        onComplete={(c, specId, stream) => { setContact(c); setSpecialtyId(specId); setCraftStream(stream); setGuidedMode('author'); setView('guided-author'); }}
        onBack={() => setView('craft')}
        onSkip={startFresh}
      />
    </>
  );

  // GUIDED AUTHORING (Conversational Phases 1–5) — the split screen: the conversation rail on the left, the live
  // CV pane (Phase 5) on the right, assembling as entries save. ≥900px = side-by-side (pane sticky+scrollable);
  // <900px = a two-tab toggle, one pane at a time, the badge carrying the progress signal. The paywall renders here
  // too (saves/edits stay craft-gated); readReloadKey re-drives a paused write after unlock. Finish → the read.
  if (view === 'guided-author') {
    const conversation = (
      <GuidedAuthor
        mode={guidedMode}
        cvId={cvId}
        specialtyId={specialtyId}
        specialtyName={specialties.find((s) => s.id === specialtyId)?.name ?? ''}
        stream={craftStream}
        sections={sections}
        reloadKey={readReloadKey}
        onCvMinted={setCvId}
        onPaywall={setPaywallCvId}
        onFinish={(id) => { setCvId(id); if (craftStream) setEvalStream(craftStream); setFreshImport(true); setView('evaluate-read'); void runEvaluateRead(id); }}
        onClassicForm={() => setView('author')}
        onSaved={(entryId) => { setLastSavedEntryId(entryId); setCvPaneKey((k) => k + 1); }}
      />
    );
    const pane = (
      <CvPane cvId={cvId} refreshKey={cvPaneKey} highlightEntryId={lastSavedEntryId} onPaywall={setPaywallCvId} onCount={setPaneCount} />
    );
    return (
      <>
        {appHeader}
        {paywall}
        {splitNarrow ? (
          <div style={{ padding: '0 var(--space-4)' }}>
            <div style={S.paneTabs} data-testid="guided-pane-tabs">
              <button className="btn btn-ghost" style={{ ...S.paneTab, ...(paneTab === 'talk' ? S.paneTabOn : {}) }} onClick={() => setPaneTab('talk')} data-testid="guided-tab-talk">Conversation</button>
              <button className="btn btn-ghost" style={{ ...S.paneTab, ...(paneTab === 'cv' ? S.paneTabOn : {}) }} onClick={() => setPaneTab('cv')} data-testid="guided-tab-cv">Your CV ({paneCount})</button>
            </div>
            {paneTab === 'talk' ? conversation : <div style={{ maxWidth: 640, margin: '16px auto' }}>{pane}</div>}
          </div>
        ) : (
          <div style={S.splitGrid} data-testid="guided-split">
            <div style={{ minWidth: 0 }}>{conversation}</div>
            <div style={S.splitPane}>{pane}</div>
          </div>
        )}
      </>
    );
  }

  // EVALUATE door — the second app's home. STUB only (P2-P5 fills it). Intentionally wired to NOTHING else:
  // no import, no craft path — the evaluate-import is a separate unbuilt flow. Honest "coming soon".
  // EVALUATE READ (P4a) — post-commit: the tagger runs (with progress), then the coverage read renders + offers
  // to keep building. evalBusy or no read yet → the tagging indicator; otherwise the read.
  if (view === 'evaluate-read') return (
    <>
      {appHeader}
      {paywall}
      {/* C3a — a non-blocking, honest note: the segmenter discarded some entries entirely on THIS import (pure
          noise it couldn't read) — so the read below only covers what came through. Never blocks the flow. */}
      {importMode === 'evaluate' && parseDropped > 0 && (
        <p style={{ maxWidth: 'min(960px, 92vw)', margin: '8px auto 0', padding: '0 var(--space-4)', fontSize: 'var(--fs-small)', color: 'var(--muted, #777)' }} data-testid="parse-dropped-note">
          {parseDropped} {parseDropped === 1 ? 'entry' : 'entries'} couldn’t be read from the file — the read below only covers what was read. Re-importing a cleaner copy usually fixes this.
        </p>
      )}
      {evalBusy || !evalRead ? (
        <ParsingIndicator stages={TAG_STAGES} />
      ) : (
        <>
          {/* C2 — a soft, non-blocking note: the tagger pass failed this time (existing tags were preserved, not
              deleted), so coverage lines may be reading off a stale/partial tag set. Re-running retries the tagger. */}
          {tagsIncomplete && (
            <p style={{ maxWidth: 'min(960px, 92vw)', margin: '8px auto 0', padding: 'var(--space-2) var(--space-4)', fontSize: 'var(--fs-small)', color: 'var(--muted, #777)' }} data-testid="tags-incomplete-note">
              Some coverage tags couldn’t be read this pass — coverage lines may be incomplete. Re-running the evaluation will retry.
            </p>
          )}
          {/* IMG-stream users: a jump link so the checklist (below the now-long read) is findable without scrolling.
              Gated alongside the read+panel: a poor parse hides the checklist, so the jump link would dangle. */}
          {evalStream === 'img' && !readGated && (
            <p style={{ maxWidth: 'min(960px, 92vw)', margin: '8px auto 0', padding: '0 var(--space-4)', fontSize: 'var(--fs-small)' }}>
              <a href="#img-stream-checklist" data-testid="img-jump" onClick={(e) => {
                // Robust scroll: the bare hash is unreliable in this state-router SPA, so scrollIntoView when the panel
                // is present. (The panel renders only when the CV's stream is 'img' — now persisted on re-import.)
                const el = document.getElementById('img-stream-checklist');
                if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
              }}><IconDown aria-hidden="true" /> Jump to the IMG-stream checklist</a>
            </p>
          )}
          {/* The section-structured QUALITY read (the substance — coverage folded in per section). The program
              layer is deferred from this v1 (it measured ~43% redundant with baseline). onGatedChange lifts the
              parse-quality gate so the sibling IMG panel can respect it (FIX 2). */}
          <RubricReadView
            cvId={cvId}
            beta
            onPaywall={setPaywallCvId}
            reloadKey={readReloadKey}
            startExtraction={freshImport}
            // A11Y-4 (round 3) — THE EMPTY STATE'S WAY OUT, FINALLY WIRED. `RubricReadView` has rendered a
            // keyboard-operable control here since R5.2, labelled with the registered sentence for the state
            // ("No review yet. Import your CV to generate one."), but it is gated on this prop and NOTHING
            // passed it — so the state the accessibility audit actually saw was a paragraph, and an applicant
            // who reached it had been told what to do with no way to do it. Only the parent knows how to get
            // back to the import flow, which is why the component asks rather than navigating itself.
            onImportCv={() => enterImport('evaluate')}
            // R5.7b — the legacy presentation's own parent-supplied pieces (owner directive: the read renders
            // through the legacy view AS IS): the specialty DISPLAY name and the two navigation callbacks,
            // the same values the legacy mount below receives.
            specialtyName={specialties.find((s) => s.id === specialtyId)?.name ?? ''}
            onHome={goToHub}
            onCraft={() => setView('author')}
            legacyView={
              <QualityReadView cvId={cvId} specialtyName={specialties.find((s) => s.id === specialtyId)?.name ?? ''} onCraft={() => setView('author')} onHome={goToHub} onGatedChange={setReadGated} onPaywall={setPaywallCvId} reloadKey={readReloadKey} />
            }
          />
          {/* Additive IMG layer — self-contained; renders only when the CV's stream is 'img' (server-gated). Hidden
              when the parse-quality warning gates the read, so a confident checklist never sits under "I couldn't read
              this well"; it reappears with the read on proceed-anyway. */}
          {!readGated && <ImgPanel cvId={cvId} />}
        </>
      )}
    </>
  );

  if (view === 'account') return <>{appHeader}<Account me={me} onBack={() => setView('author')} onDelete={deleteAccount} /></>;

  // Evaluate/import — the shared import state machine. !importedText → ingest (ImportCv); EVALUATE inserts a
  // specialty step (pendingText) before parse; parsing → spinner; candidates ready → confirm; parse failure → error.
  if (view === 'import') {
    const evalSpecName = specialties.find((s) => s.id === evalSpecialtyId)?.name ?? '';
    // Legible wait: name the specialty in the parse spinner (evaluate only; craft keeps the default PARSE_STAGES).
    // 6 stages so they advance across the longer (up to 85s) parse — paired with PARSE_INTERVAL below (not static).
    const parseStages = evalSpecName
      ? ['Reading your CV…', `Lining it up against what ${evalSpecName} programs tend to look for…`, 'Finding each entry…', 'Sorting them into sections…', 'Pulling out the details…', 'Almost there — a longer CV can take up to a minute…']
      : undefined;
    return (
    <>
      {appHeader}
      {importMode === 'evaluate' && pendingText && !candidates && !importBusy && !error ? (
        // EVALUATE: specialty FIRST (before parse) so the wait is legible. Back → re-ingest (clear the held text).
        <EvalSetup specialties={specialties} onStart={confirmEvalSpecialty} onBack={() => setPendingText(null)} />
      ) : !importedText ? (
        <ImportCv onReady={onImportReady} onCancel={() => { setImportedText(''); setCandidates(null); setError(''); setPendingText(null); setView('author'); }} />
      ) : importMode === 'evaluate' && pendingEvalCommit && importExceptions ? (
        // G1-1c — EVALUATE with exceptions: the pre-read panel gates the held auto-adopt commit. No exceptions →
        // this branch never renders and the auto-adopt already ran (unchanged fast path).
        <ImportExceptions
          contactCandidate={importExceptions.contactCandidate}
          missingFields={{ name: !contact.name.trim(), phone: !contact.phone.trim(), city: !contact.city.trim() }}
          sourceText={importExceptions.sourceText}
          chunks={importExceptions.chunks}
          duplicateSuspects={importExceptions.duplicateSuspects}
          onConfirmContact={confirmContactCandidate}
          onContinue={continueEvalImportExceptions}
        />
      ) : importBusy && !candidates ? (
        <ParsingIndicator stages={parseStages} intervalMs={11000} /> /* parse phase (no candidates yet) — slow advance over the up-to-85s wait */
      ) : error ? (
        <main style={S.main} data-testid="import-error-panel">
          <p style={S.err}>{error}</p>
          <button className="btn btn-secondary" onClick={() => { setImportedText(''); setError(''); }} data-testid="import-parse-retry"><IconBack aria-hidden="true" /> Back</button>
        </main>
      ) : candidates && candidates.length === 0 ? (
        <main style={S.main} data-testid="import-scramble">
          <h1>We couldn’t separate this into entries</h1>
          <p>This often happens with two-column or PDF-pasted CVs, where the text comes through scrambled. Try pasting from a single-column version of your CV, or paste each section separately.</p>
          <button className="btn btn-secondary" onClick={() => { setImportedText(''); setCandidates(null); }} data-testid="import-scramble-back"><IconBack aria-hidden="true" /> Back</button>
        </main>
      ) : candidates ? (
        <>
          {/* CRAFT with exceptions: same panel content shown ABOVE the existing confirm list — no flow change. */}
          {importMode === 'craft' && importExceptions && (
            <ImportExceptions
              contactCandidate={importExceptions.contactCandidate}
              missingFields={{ name: !contact.name.trim(), phone: !contact.phone.trim(), city: !contact.city.trim() }}
              sourceText={importExceptions.sourceText}
              chunks={importExceptions.chunks}
              duplicateSuspects={importExceptions.duplicateSuspects}
              onConfirmContact={confirmContactCandidate}
              onContinue={dismissCraftImportExceptions}
            />
          )}
          <ImportConfirm
            candidates={candidates}
            specialties={specialties}
            sections={sections}
            busy={importBusy}
            error={error}
            onCommit={commitImport}
            // EVALUATE: specialty already chosen up front → show it, don't re-ask. CRAFT keeps the in-confirm picker.
            lockedSpecialty={importMode === 'evaluate' && evalSpecialtyId ? { id: evalSpecialtyId, name: evalSpecName } : undefined}
            onCancel={() => { setImportedText(''); setCandidates(null); setError(''); }} /* back to ingest (re-paste) */
          />
        </>
      ) : (
        <ParsingIndicator stages={parseStages} intervalMs={11000} /> /* transient before parseImport resolves */
      )}
    </>
    );
  }

  if (view === 'review' && cvId) return <>{appHeader}{paywall}<CvWorkspace cvId={cvId} onBack={() => setView('author')} onAddForGap={onAddForGap} onPaywall={setPaywallCvId} reloadKey={readReloadKey} /></>;

  // "Your CVs" must always land somewhere REAL. This used to require MORE THAN ONE cv, which made the header's
  // "Your CVs" a DEAD END for a single-CV account: goToPicker clears cvId and re-arms the picker, the picker
  // didn't render, and the user fell through to the bare authoring form — no go-review / go-evaluation buttons
  // (both are cvId-gated) and no way back to the CV without a reload. ONE cv now renders a one-row library.
  // This does NOT re-trigger auto-routing: the Phase-6 auto-restore fires only on the `authed` transition, so it
  // cannot fight this explicit navigation (and it sets cvId, which keeps the picker out of the boot path).
  if (cvList && cvList.length >= 1 && !cvId && !dismissedPicker) {
    return (
      <>
        {appHeader}
        {paywall}
        <main style={S.main} data-testid="cv-picker">
          <h1>Your CVs</h1>
          <p style={{ color: 'var(--muted)' }}>
            {cvList.length === 1
              ? 'One saved — open its review, keep building, or start a new one.'
              : `${cvList.length} saved — open a review, keep building, or start a new one.`}
          </p>
          {/* Calm card list (Principle 5): per CV, the name + a quiet assessment line + Review / Continue. */}
          {cvList.map((cv) => (
            <div key={cv.id} className="card-interactive" style={S.cvCard} data-testid={`cv-card-${cv.id}`}>
              <div style={{ minWidth: 0 }}>
                <div style={S.cvCardName}>{cv.name || cv.specialty_name}</div>
                <div className="tnum" style={S.cvCardMeta}>
                  {cv.specialty_name} · {cv.entry_count} {cv.entry_count === 1 ? 'entry' : 'entries'} · {fmtDate(cv.last_edited)}
                </div>
                <div className="tnum" style={S.cvCardBadge} data-testid={`cv-assess-${cv.id}`}>
                  {cv.has_assessment
                    ? <>Reviewed{cv.assessed_at ? ` ${fmtDate(cv.assessed_at)}` : ''}{cv.stale_hint ? ' · edited since' : ''}</>
                    : 'Not yet reviewed'}
                </div>
              </div>
              <div style={S.cvCardActions}>
                {cv.entry_count > 0 && <button className="btn btn-secondary" style={{ fontSize: 'var(--fs-small)' }} onClick={() => reviewCv(cv)} data-testid={`review-${cv.id}`}>Review</button>}
                <button className="btn btn-primary" style={{ fontSize: 'var(--fs-small)' }} onClick={() => resumeCv(cv)} data-testid={`resume-${cv.id}`}>Continue in craft</button>
              </div>
            </div>
          ))}
          <div style={{ marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={() => setDismissedPicker(true)} data-testid="start-new">Start a new CV</button>
            <button className="btn btn-secondary" onClick={() => enterImport('craft')} data-testid="import-cv-from-picker">Import an existing CV</button>
          </div>
        </main>
      </>
    );
  }

  const canSave = !!specialtyId && !!sectionId && (!!title.trim() || bullets.length > 0 || !!draft.trim());

  return (
    <>
    {appHeader}
    {paywall}
    <main style={S.main}>
      <h1>CVCraft — build your CV</h1>
      {/* fix/empty-cv-affordances — these two only render when they lead somewhere real: an EMPTY cv has nothing to
          review, and a cv with no stored read has no evaluation to view (the buttons previously rendered on bare
          cvId, pointing an empty-CV user at dead ends — the owner's own refresh-landing report, 2026-08-04). */}
      {cvId && (cvList?.find((c) => c.id === cvId)?.entry_count ?? 0) > 0 && <button className="btn btn-primary" onClick={() => setView('review')} data-testid="go-review">Review &amp; edit your CV</button>}
      {cvId && cvList?.find((c) => c.id === cvId)?.has_assessment && <button className="btn btn-secondary" onClick={() => { setFreshImport(false); setView('evaluate-read'); void openStoredRead(cvId); }} data-testid="go-evaluation">View your evaluation</button>}
      {!cvId && <button className="btn btn-secondary" onClick={() => enterImport('craft')} data-testid="import-cv-from-author">Import an existing CV</button>}

      <label style={S.label}>Specialty
        <select style={S.input} value={specialtyId} onChange={(e) => setSpecialtyId(e.target.value)} data-testid="specialty">
          <option value="">— choose —</option>
          {specialties.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </label>

      {specialtyId && (
        <fieldset style={S.fs}>
          <legend>Add an entry</legend>
          {gapPrompt && (
            <p style={S.gapBanner} data-testid="gap-prompt">
              Adding an entry to show <strong>{gapPrompt}</strong> — list experience you already have that isn’t on your CV yet (only what you’ve actually done).
              <button type="button" style={S.gapDismiss} onClick={() => setGapPrompt('')} aria-label="Dismiss" data-testid="gap-dismiss"><IconClose aria-hidden="true" /></button>
            </p>
          )}
          <style>{`@keyframes cvc-ping{0%{transform:scale(1);opacity:.7}75%,100%{transform:scale(2.4);opacity:0}}`}</style>
          <label style={S.label}>Describe it in plain language (optional — type, or speak with the mic — we'll fill the fields for you to check)
            <textarea style={S.input} rows={2} value={describe} placeholder="e.g. I attended Hussey College between 2008 and 2010" onChange={(e) => setDescribe(e.target.value)} data-testid="describe" />
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', margin: 'var(--space-1) 0' }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 0, ...(recording ? { background: 'var(--danger-surface)', borderColor: 'var(--danger)', color: 'var(--danger)' } : {}) }}
              onClick={recording ? stopRecording : startRecording}
              disabled={transcribing}
              aria-label={recording ? `Recording ${fmtTime(recSeconds)}. Tap to stop.` : 'Record your description with the microphone'}
              aria-pressed={recording}
              data-testid="mic"
            >{transcribing ? 'Transcribing…' : recording ? <><IconStop aria-hidden="true" /> Stop</> : <><IconMic aria-hidden="true" /> Record</>}</button>
            {recording && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--fs-small)', color: 'var(--danger)' }} role="status" data-testid="rec-indicator">
                <span style={{ position: 'relative', display: 'inline-flex', width: 10, height: 10 }}>
                  <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--danger)', opacity: 0.7, animation: 'cvc-ping 1s ease-out infinite' }} />
                  <span style={{ position: 'relative', display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: 'var(--danger)' }} />
                </span>
                Recording {fmtTime(recSeconds)} · tap to stop
              </span>
            )}
          </div>
          {micError && <p style={{ ...S.err, fontSize: 'var(--fs-small)', margin: 'var(--space-1) 0' }} data-testid="mic-error">{micError}</p>}
          <button className="btn btn-secondary" onClick={runExtract} disabled={extracting || !describe.trim()} data-testid="extract">{extracting ? 'Reading…' : 'Read & fill the fields'}</button>
          <p style={S.help}>We only fill in the facts you stated — check and edit everything below before saving. Anything you described doing is dropped into the bullet box for you to write up yourself.</p>

          <label style={S.label}>Section
            <select style={S.input} value={sectionId} onChange={(e) => { setSectionId(e.target.value); setResult(null); }} data-testid="section">
              <option value="">— choose a section —</option>
              {sections.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.archetype})</option>)}
            </select>
          </label>

          {section && (
            <>
              <p style={S.badge} data-testid="archetype">{isCredential ? 'Credential — enter the facts (degree, award, or citation). No impact bullets.' : 'Experience — describe what you did and what changed.'}</p>
              <label style={S.label}>{isCredential ? 'Title (degree / award / paper title)' : 'Title / role'}<input style={S.input} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="title" /></label>
              <label style={S.label}>{isCredential ? 'Granting body / venue' : 'Organization'}<input style={S.input} value={organization} onChange={(e) => setOrganization(e.target.value)} data-testid="organization" /></label>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <label style={{ ...S.label, flex: 1 }}>Start<input style={S.input} value={startDate} placeholder="2008" onChange={(e) => { setStartDate(e.target.value); if (e.target.value) setDateText(''); }} data-testid="start-date" /></label>
                <label style={{ ...S.label, flex: 1 }}>End<input style={S.input} value={endDate} placeholder="2010 / present" onChange={(e) => { setEndDate(e.target.value); if (e.target.value) setDateText(''); }} data-testid="end-date" /></label>
                <label style={{ ...S.label, flex: 1 }}>or date (as text)<input style={S.input} value={dateText} placeholder="summer 2019" onChange={(e) => setDateText(e.target.value)} data-testid="date-text" /></label>
              </div>

              {!isCredential && (
                <div style={S.guide} data-testid="verb-guidance">
                  <strong>Verb guidance</strong> (examples only — pick the level that’s true and <em>type your own verb</em>; we never write it for you):
                  <ul style={{ margin: 'var(--space-2) 0 0' }}>{VERB_GUIDANCE.map((v) => <li key={v.level}><strong>{v.level}:</strong> {v.examples}</li>)}</ul>
                </div>
              )}

              <label style={S.label}>{isCredential ? 'Citation / description (optional — your own words)' : 'Write a bullet — in your own words'}
                <textarea style={S.ta} rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="bullet" />
              </label>
              <div>
                <button className="btn btn-secondary" onClick={checkBullet} disabled={!draft.trim() || checking} data-testid="check">{checking ? 'Checking…' : isCredential ? 'Check citation' : 'Check this bullet'}</button>
                <button className="btn btn-secondary" onClick={addBullet} disabled={!draft.trim()} data-testid="add">Add line</button>
              </div>

              {result && (
                <div style={S.diag} data-testid="diagnostics">
                  {result.overall === 'pass'
                    ? <p data-testid="passed"><IconCheck aria-hidden="true" /> Nothing flagged.</p>
                    : <>
                        <p><strong>A few questions</strong> (your words, not ours):</p>
                        <ul>
                          {result.ladder.status === 'flag' && <li data-testid={`gate-${result.ladder.gate_id}`}><em>{result.ladder.diagnosis}</em><br />{result.ladder.question}
                            {isQuantityAsk(result.ladder.question) && <p style={S.quantityHint} data-testid="quantity-hint">{QUANTITY_HINT_COPY}</p>}
                          </li>}
                          {result.integrity.map((g) => <li key={g.gate_id} data-testid={`gate-${g.gate_id}`}><em>{g.diagnosis}</em><br />{g.question}
                            {isQuantityAsk(g.question) && <p style={S.quantityHint} data-testid="quantity-hint">{QUANTITY_HINT_COPY}</p>}
                          </li>)}
                        </ul>
                      </>}
                </div>
              )}

              {bullets.length > 0 && <ul data-testid="pending-lines">{bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>}
              <button className="btn btn-primary" onClick={saveEntry} disabled={!canSave} data-testid="save">Save entry</button>
            </>
          )}
          {error && <p style={S.err}>{error}</p>}
        </fieldset>
      )}

      {saved.length > 0 && (
        <section data-testid="cv">
          <h2>Your CV so far ({saved.length} {saved.length === 1 ? 'entry' : 'entries'})</h2>
          {sections.filter((sec) => saved.some((e) => e.section === sec.id)).map((sec) => (
            <div key={sec.id} style={S.sectionBlock} data-testid={`cv-section-${sec.id}`}>
              <h3 style={{ marginBottom: 'var(--space-1)' }}>{sec.name} <span style={S.tag}>{sec.archetype}</span></h3>
              {saved.filter((e) => e.section === sec.id).map((e, i) => (
                <div key={i} style={{ marginBottom: 'var(--space-2)' }}>
                  <strong>{e.title}</strong>{e.organization ? ` — ${e.organization}` : ''}
                  {e.bullets.length > 0 && <ul style={{ margin: '2px 0' }}>{e.bullets.map((b, j) => <li key={j}>{b}</li>)}</ul>}
                </div>
              ))}
            </div>
          ))}

          <fieldset style={S.fs} data-testid="export">
            <legend>Download your CV</legend>
            <div style={S.guide}>Contact header (appears at the top of your CV):</div>
            <label style={S.label}>Full name<input style={S.input} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name" /></label>
            <label style={S.label}>Phone<input style={S.input} value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} /></label>
            <label style={S.label}>City<input style={S.input} value={contact.city} onChange={(e) => setContact({ ...contact, city: e.target.value })} /></label>
            <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center', margin: 'var(--space-2) 0' }}>
              <label style={{ fontSize: 'var(--fs-body)' }}>Format
                <select style={{ ...S.input, width: 'auto', marginLeft: 6 }} value={exportFormat} onChange={(e) => setExportFormat(e.target.value as 'docx' | 'pdf')} data-testid="export-format">
                  <option value="docx">Word (.docx) — editable</option>
                  <option value="pdf">PDF — for submission</option>
                </select>
              </label>
              <label style={{ fontSize: 'var(--fs-body)' }} data-testid="crafted-toggle-label">
                <input type="checkbox" checked={addCrafted} onChange={(e) => setAddCrafted(e.target.checked)} data-testid="crafted-toggle" /> add a "Crafted [date]" line
              </label>
            </div>
            <button className="btn btn-primary" onClick={downloadCv} disabled={!cvId} data-testid="download">Download CV ({exportFormat === 'pdf' ? '.pdf' : '.docx'})</button>
          </fieldset>
        </section>
      )}
    </main>
    </>
  );
}

const S: Record<string, CSSProperties> = {
  main: { maxWidth: 660, margin: '40px auto', padding: '0 var(--space-4)', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-body)' },
  // Conversational Phase 5 — the split screen (≥900px) + the narrow-mode tab strip (<900px).
  splitGrid: { display: 'grid', gridTemplateColumns: 'minmax(0, 46fr) minmax(0, 54fr)', gap: 'var(--space-5)', maxWidth: 1280, margin: '0 auto', padding: '0 var(--space-5)', alignItems: 'start' },
  splitPane: { position: 'sticky', top: 16, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', marginTop: 40 },
  paneTabs: { display: 'flex', gap: 'var(--space-1)', borderBottom: '1px solid var(--rule)', margin: 'var(--space-3) 0 0', fontFamily: 'var(--sans)' },
  paneTab: { fontSize: 'var(--fs-body)', padding: 'var(--space-2) var(--space-4)', borderRadius: 0, color: 'var(--muted)' },
  paneTabOn: { color: 'var(--ink)', fontWeight: 600, borderBottom: '2px solid var(--ink)', marginBottom: -1 },
  // Persistent app header (authenticated views). Mirrors the Landing header: rule under, card bg, 56px, brand left.
  appHeader: { borderBottom: '1px solid var(--rule)', background: 'var(--card)', padding: '0 var(--space-5)', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  brandBtn: { fontFamily: 'var(--serif)', fontSize: 20, color: 'var(--ink)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' },
  headerActions: { display: 'flex', alignItems: 'center', gap: 'var(--space-1)' },
  headerBtn: { margin: 0, fontSize: 'var(--fs-small)' },
  headerEmail: { margin: 0, fontSize: 'var(--fs-small)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  label: { display: 'block', margin: 'var(--space-3) 0' },
  input: { display: 'block', width: '100%', padding: 'var(--space-2)', marginTop: 2 },
  fs: { margin: 'var(--space-4) 0', padding: 'var(--space-3)' },
  badge: { background: 'var(--surface-subtle)', color: 'var(--ink-subtle)', padding: 'var(--space-2) var(--space-3)', borderRadius: 6, fontSize: 'var(--fs-body)' },
  gapBanner: { background: 'var(--surface-subtle)', border: '1px solid var(--rule)', borderRadius: 6, padding: 'var(--space-2) var(--space-3)', margin: 'var(--space-2) 0', fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)', position: 'relative' },
  gapDismiss: { background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 'var(--fs-h3)', lineHeight: 1, padding: '0 0 0 var(--space-2)' },
  guide: { background: 'var(--surface-subtle)', padding: 'var(--space-3)', borderRadius: 6, fontSize: 'var(--fs-body)', margin: 'var(--space-3) 0' },
  help: { fontSize: 'var(--fs-label)', color: 'var(--muted)', margin: 'var(--space-1) 0 var(--space-3)' },
  ta: { width: '100%', padding: 'var(--space-2)', marginTop: 2, fontFamily: 'inherit' },
  diag: { background: 'var(--warn-surface)', padding: 'var(--space-3)', borderRadius: 6, marginTop: 'var(--space-3)' },
  // cc-11 round 14 — same treatment as QualityReadView's `quantityHint`: quiet, italic, clearly product copy.
  quantityHint: { margin: 'var(--space-1) 0 0', color: 'var(--ink-subtle)', fontSize: 'var(--fs-small)', fontStyle: 'italic' },
  sectionBlock: { borderLeft: '3px solid var(--rule)', paddingLeft: 10, margin: 'var(--space-3) 0' },
  tag: { fontSize: 'var(--fs-label)', color: 'var(--muted)', fontWeight: 400 },
  err: { color: 'var(--danger)' },
  // B3 "Your CVs" library card — calm, a quiet row per CV (not a dashboard tile).
  cvCard: { boxShadow: 'var(--elev-raised)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap', padding: 'var(--space-3) var(--space-4)', margin: 'var(--space-3) 0', border: '1px solid var(--rule)', borderRadius: 8, background: 'var(--card)' },
  cvCardName: { fontWeight: 600, color: 'var(--ink)' },
  cvCardMeta: { fontSize: 'var(--fs-small)', color: 'var(--muted)', marginTop: 2 },
  cvCardBadge: { fontSize: 'var(--fs-label)', color: 'var(--ink-subtle)', marginTop: 'var(--space-1)' },
  cvCardActions: { display: 'flex', gap: 'var(--space-2)', flexShrink: 0 },
};

// A short, calm date label for the library cards (the CV's last-edited / reviewed timestamp).
function fmtDate(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 86_400_000) return 'today';
  if (diff < 172_800_000) return 'yesterday';
  return new Date(ms).toLocaleDateString();
}
